/**
 * Standard filter sync (Department, Gender, Color, Category, etc.)
 *
 * Mirrors the grouped-size filter approach: with the sidebar preserved across AJAX,
 * checkbox state and active-tag pills must be updated imperatively in the DOM.
 *
 * Does NOT modify mm-grouped-size-filter.liquid — grouped size / parent-child tags
 * are detected via DOM attributes and left to the existing size filter handlers.
 *
 * ## Ordering contract (do not break silently)
 *
 * Correctness depends on event phase/order and on item-grid.js preserving the sidebar
 * across AJAX filter updates. Changing any of the following without updating the rest
 * can break tag/checkbox sync; there is no automated test coverage for this wiring.
 *
 * 1. Capture phase and registration order
 *    - Three document capture listeners: click (active-tag ×), input (standard checkboxes),
 *      input (grouped-size checkboxes for collapsible sync).
 *    - Tag × must run before item-grid.js bubble handlers and grouped-size inline handlers;
 *      uses stopImmediatePropagation() after updating DOM state.
 *    - Standard checkbox input must run before item-grid onFormSubmit so tags exist before
 *      AJAX. item-grid.js skips renderActiveTag when window.__syncFilterVisualState is
 *      defined (skipActiveTagRender guard).
 *
 * 2. Mobile: window before document
 *    - collection-mobile-filters.js registers active-tag × on window capture so it runs
 *      before this file's document capture and desktop grouped-size handlers.
 *
 * 3. Sidebar preserve invariant (item-grid.js)
 *    - onReplaceAjaxContent swaps only .grid__item--content, not #CollectionSidebarFilterWrap.
 *      Replacing the sidebar re-renders filters from a narrowed Shopify context and breaks
 *      grouped-size department groups plus imperative sync state.
 *
 * 4. Grouped size boundary
 *    - Tags/inputs owned by mm-grouped-size-filter (data-gsz-*, grouped-size-input) are
 *      ignored here via isGroupedSizeTag() and input class checks.
 *
 * ## Load scope
 *
 * Loaded on collection/search templates only (layout/theme.liquid). No-ops when no
 * .filter-form exists in the DOM at script execution time.
 *
 * ## Manual QA checklist
 *
 * - Desktop: add/remove standard filter via checkbox; tag × removes filter and reloads grid
 * - Desktop: price range × pill clears filter via url_to_remove (not FormData)
 * - Desktop: grouped size + standard filters combined; each tag type removes correctly
 * - Desktop: collapse_filters on — inactive filters collapsed; active filter expanded
 * - Desktop: collapse_filters off — all visible filters expanded
 * - Desktop: Category selected — Size filter silently narrows to category departments (e.g. Shoes → Footwear)
 * - Mobile: checkbox changes do not reload until Apply; tag × removes without reload
 * - Mobile: Apply reloads; all size department groups still visible after size filter
 * - JS disabled: checkboxes still submit via native form (graceful degradation)
 */
(function () {
  'use strict'

  var STANDARD_TAG_ATTR = 'data-filter-managed'
  var GSZ_TAG_ATTR = 'data-gsz-managed'
  var ACTIVE_TAG = 'tag--active'
  var AVAILABILITY_PARAM = 'filter.v.availability'
  var DEPT_PARAM = 'filter.p.m.custom.department'
  var PRICE_GTE_PARAM = 'filter.v.price.gte'
  var PRICE_LTE_PARAM = 'filter.v.price.lte'
  /** Latest refreshStyleFilterOptions wins; older fetches still complete but are ignored (not aborted). */
  var refreshStyleRequestId = 0

  /**
   * Coalesce rapid category toggles into one desktop Size scope fetch.
   * Each distinct category combo may trigger a section render (cached by key after first hit).
   */
  var SIZE_SCOPE_DEBOUNCE_MS = 150
  var sizeScopeDebounceTimer = null
  var sizeScopeRequestId = 0
  /** Set when Size scoping prunes checkboxes while item-grid is still animating. */
  var pendingGridReloadAfterSizeScope = false
  var pendingGridReloadFlushTimer = null
  var PENDING_GRID_RELOAD_POLL_MS = 50
  var PENDING_GRID_RELOAD_MAX_ATTEMPTS = 40
  /** @type {Map<string, { count: number, html: Element }>} */
  var fullSizeFilterCache = new Map()
  /** @type {Map<string, Element>} */
  var filterWrapperSectionCache = new Map()

  function isAvailabilityFilter(paramName) {
    return paramName === AVAILABILITY_PARAM
  }

  function isAvailabilityActiveTag(tag) {
    if (!tag) return false
    if (tag.getAttribute('data-filter-name') === AVAILABILITY_PARAM) return true
    // Do not match on url_to_remove containing availability — every collection pill href
    // keeps filter.v.availability=1 in this store (see assets/mm-theme.js).
    var link = tag.querySelector('a')
    return link ? /in stock/i.test((link.textContent || '').trim()) : false
  }

  /** Attribute-less active tag in a price filter context (legacy Liquid pill). */
  function isLegacyServerPriceActiveTag(li) {
    if (!li || li.classList.contains('hide')) return false
    if (li.getAttribute('data-filter-name') || li.hasAttribute('data-dept-tag')) return false
    if (li.getAttribute('data-price-managed') === 'true') return false
    if (li.getAttribute('data-gsz-size') || li.getAttribute('data-filter-managed') === 'true') {
      return false
    }

    var wrapper = li.closest('.filter-wrapper')
    if (!wrapper || !wrapper.querySelector('.price-range')) return false

    var link = li.querySelector('a')
    if (!link) return false
    if (/in stock/i.test((link.textContent || '').trim())) return false

    return true
  }

  function removePriceActiveTagsFromList(list) {
    if (!list) return
    list.querySelectorAll('.tag--remove').forEach(function (tag) {
      if (isPriceActiveTag(tag) || isLegacyServerPriceActiveTag(tag)) tag.remove()
    })
  }

  /** Price pills have no data-filter-* attrs; removal must use url_to_remove, not FormData. */
  function isPriceActiveTag(li) {
    if (!li || li.getAttribute('data-filter-name') || li.hasAttribute('data-dept-tag')) return false
    if (li.getAttribute('data-price-managed') === 'true') return true
    if (isLegacyServerPriceActiveTag(li)) return true
    var link = li.querySelector('a')
    if (!link) return false
    var href = link.getAttribute('href')
    if (!href) return false
    if (/in stock/i.test((link.textContent || '').trim())) return false
    try {
      var current = new URLSearchParams(window.location.search)
      var remove = new URLSearchParams(new URL(link.href, window.location.origin).search)
      var hadPrice = current.has(PRICE_GTE_PARAM) || current.has(PRICE_LTE_PARAM)
      var removeHasPrice = remove.has(PRICE_GTE_PARAM) || remove.has(PRICE_LTE_PARAM)
      return hadPrice && !removeHasPrice
    } catch (e) {
      return false
    }
  }

  function stripAllPriceParams(params) {
    var keysToRemove = []
    params.forEach(function (value, key) {
      if (
        key === PRICE_GTE_PARAM ||
        key === PRICE_LTE_PARAM ||
        key.indexOf('filter.v.price.') === 0
      ) {
        keysToRemove.push(key)
      }
    })
    keysToRemove.forEach(function (key) {
      params.delete(key)
    })
    return params
  }

  function buildPriceRemoveSearchParams() {
    return stripAllPriceParams(new URLSearchParams(window.location.search))
  }

  function syncUrlWithoutPriceParams() {
    var params = buildPriceRemoveSearchParams()
    var query = params.toString()
    history.replaceState(
      {},
      '',
      query ? window.location.pathname + '?' + query : window.location.pathname
    )
    return params
  }

  function setPriceInputsDisabled(container, disabled) {
    queryScopeForms(container).forEach(function (form) {
      form.querySelectorAll('.price-range input[name]').forEach(function (input) {
        input.disabled = disabled
      })
    })
  }

  function buildPriceRemoveHref() {
    var params = buildPriceRemoveSearchParams()
    var query = params.toString()
    return query ? window.location.pathname + '?' + query : window.location.pathname
  }

  /** Flat money label for price pills; matches price-range.js / theme product pricing. */
  function normalizePriceFilterAmount(val) {
    if (val == null || val === '') return ''
    var s = String(val).trim()
    if (s.indexOf('.') === -1) s = s + '.00'
    return s
  }

  function formatPriceFilterMoney(amount, moneyFormat) {
    amount = normalizePriceFilterAmount(amount)
    if (!amount) return ''
    if (typeof amount === 'string') amount = amount.replace('.', '')

    var formatString = moneyFormat || '${{amount}}'
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    var match = formatString.match(placeholderRegex)
    if (!match) return String(amount)

    var number = parseFloat(amount)
    if (isNaN(number)) return String(amount)
    number = (number / 100).toFixed(2)
    var parts = number.split('.')
    var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
    var centsAmount = parts[1] ? '.' + parts[1] : ''

    var value = ''
    switch (match[1]) {
      case 'amount':
        value = dollarsAmount + centsAmount
        break
      case 'amount_no_decimals':
        value = dollarsAmount
        break
      case 'amount_with_comma_separator':
        value = dollarsAmount.replace(/,/g, '.') + (parts[1] ? ',' + parts[1] : '')
        break
      default:
        value = dollarsAmount + centsAmount
    }

    return formatString.replace(placeholderRegex, value)
  }

  function formatPriceFilterLabel(form, minVal, maxVal) {
    var priceRangeEl = form.querySelector('price-range.price-range')
    if (priceRangeEl && typeof priceRangeEl.formatFilterLabel === 'function') {
      return priceRangeEl.formatFilterLabel(minVal, maxVal)
    }

    var moneyFormat =
      (priceRangeEl && priceRangeEl.getAttribute('data-money-format')) ||
      (form.querySelector('.price-range') &&
        form.querySelector('.price-range').getAttribute('data-money-format'))

    if (!moneyFormat) return ''

    if (minVal && maxVal) {
      return (
        formatPriceFilterMoney(minVal, moneyFormat) +
        ' - ' +
        formatPriceFilterMoney(maxVal, moneyFormat)
      )
    }
    if (minVal) return formatPriceFilterMoney(minVal, moneyFormat) + '+'
    if (maxVal) return 'Up to ' + formatPriceFilterMoney(maxVal, moneyFormat)
    return ''
  }

  function getPriceRangeBounds(form) {
    if (!form) return null
    var priceRangeEl = form.querySelector('price-range.price-range') || form.querySelector('.price-range')
    if (!priceRangeEl) return null

    return {
      min: parseFloat(priceRangeEl.getAttribute('data-min')) || 0,
      max:
        parseFloat(priceRangeEl.getAttribute('data-max')) ||
        parseFloat(priceRangeEl.getAttribute('data-max-value')) ||
        100
    }
  }

  function isPriceSelectionActive(minVal, maxVal, bounds) {
    var hasMin = minVal != null && String(minVal).trim() !== ''
    var hasMax = maxVal != null && String(maxVal).trim() !== ''
    if (!hasMin && !hasMax) return false
    if (!bounds) return true

    var min = hasMin ? parseFloat(minVal) : bounds.min
    var max = hasMax ? parseFloat(maxVal) : bounds.max
    if (isNaN(min) || isNaN(max)) return true

    // Full catalog range is the unfiltered default — not an active price filter.
    return !(min <= bounds.min && max >= bounds.max)
  }

  function getPriceRangeLabel(form, pendingOverride, trustPendingOnly) {
    if (!form) return ''
    var priceRange = form.querySelector('.price-range')
    if (!priceRange) return ''

    var minVal = pendingOverride && pendingOverride.min != null ? String(pendingOverride.min).trim() : ''
    var maxVal = pendingOverride && pendingOverride.max != null ? String(pendingOverride.max).trim() : ''

    if (!minVal && !maxVal && !trustPendingOnly) {
      var minInput = priceRange.querySelector('.price-range__input-min')
      var maxInput = priceRange.querySelector('.price-range__input-max')
      minVal = minInput && String(minInput.value).trim()
      maxVal = maxInput && String(maxInput.value).trim()
    }

    if (!isPriceSelectionActive(minVal, maxVal, getPriceRangeBounds(form))) return ''

    var formatted = formatPriceFilterLabel(form, minVal, maxVal)
    if (formatted) return formatted

    var minDisplay = priceRange.querySelector('.price-range__display-min')
    var maxDisplay = priceRange.querySelector('.price-range__display-max')
    var minText = minDisplay ? minDisplay.textContent.trim() : minVal
    var maxText = maxDisplay ? maxDisplay.textContent.trim() : maxVal

    if (minVal && maxVal) return minText + ' - ' + maxText
    if (minVal) return minText + '+'
    return 'Up to ' + maxText
  }

  function syncPriceActiveTag(wrapper, options) {
    if (!wrapper) return
    options = options || {}

    var form =
      wrapper.querySelector('.filter-form:not([data-mobile-filter])') ||
      wrapper.querySelector('.filter-form')
    if (!form || !form.querySelector('.price-range')) return

    var list = getOrCreateActiveTagsList(wrapper)
    removePriceActiveTagsFromList(list)

    var label = getPriceRangeLabel(form, options.pendingPriceRange, options.mobile)
    if (!label) return

    var li = document.createElement('li')
    li.className = 'tag tag--remove'
    li.setAttribute('data-price-managed', 'true')

    var a = document.createElement('a')
    a.className = 'btn btn--small js-no-transition'
    a.href = options.mobile ? '#' : buildPriceRemoveHref()
    a.textContent = label
    li.appendChild(a)
    li.appendChild(cloneCloseIcon())
    list.appendChild(li)
  }

  function syncDesktopPriceActiveTags(root) {
    if (!isDesktopSidebarContext()) return
    root = root || document
    var wrapper = root.querySelector('#CollectionSidebarFilterWrap .filter-wrapper')
    if (!wrapper) return

    var params = new URLSearchParams(window.location.search)
    var hasPrice = params.has(PRICE_GTE_PARAM) || params.has(PRICE_LTE_PARAM)

    if (!hasPrice) {
      resetPriceRangeUI(wrapper)
    } else {
      applyPriceRangeValues(
        wrapper,
        params.get(PRICE_GTE_PARAM),
        params.get(PRICE_LTE_PARAM)
      )
    }

    syncPriceActiveTag(wrapper)

    wrapper.querySelectorAll('.tag--remove[data-price-managed="true"] a').forEach(function (anchor) {
      anchor.href = buildPriceRemoveHref()
    })
  }

  function applyPriceRangeValues(container, min, max) {
    queryScopeForms(container).forEach(function (form) {
      var priceRangeEl = form.querySelector('price-range.price-range')
      if (!priceRangeEl) return

      var bounds = getPriceRangeBounds(form)
      if (!isPriceSelectionActive(min, max, bounds)) return

      if (typeof priceRangeEl.setValues === 'function') {
        priceRangeEl.setValues(min, max)
        return
      }

      var minVal = min != null && min !== '' ? parseFloat(min) : bounds.min
      var maxVal = max != null && max !== '' ? parseFloat(max) : bounds.max
      priceRangeEl.setAttribute('data-min-value', min != null && min !== '' ? String(min) : '')
      priceRangeEl.setAttribute('data-max-value', max != null && max !== '' ? String(max) : '')

      var sliderEl = priceRangeEl.querySelector('.price-range__slider')
      if (sliderEl && sliderEl.noUiSlider) {
        window.__mmSuppressPriceRangeChange = true
        try {
          sliderEl.noUiSlider.set([minVal, maxVal], true)
        } finally {
          window.__mmSuppressPriceRangeChange = false
        }
      }

      form.querySelectorAll('.price-range input[name]').forEach(function (input) {
        if (input.name.indexOf('gte') !== -1) {
          input.value = min != null && min !== '' ? String(min) : ''
        } else if (input.name.indexOf('lte') !== -1) {
          input.value = max != null && max !== '' ? String(max) : ''
        }
      })
    })
  }

  function resetPriceRangeUI(container) {
    queryScopeForms(container).forEach(function (form) {
      var priceRangeEl = form.querySelector('price-range.price-range')
      if (!priceRangeEl) return

      var minRange = parseFloat(priceRangeEl.getAttribute('data-min')) || 0
      var maxRange =
        parseFloat(priceRangeEl.getAttribute('data-max')) ||
        parseFloat(priceRangeEl.getAttribute('data-max-value')) ||
        100

      priceRangeEl.setAttribute('data-min-value', '')
      priceRangeEl.setAttribute('data-max-value', '')

      form.querySelectorAll('.price-range input[name]').forEach(function (input) {
        input.value = ''
      })

      var sliderEl = priceRangeEl.querySelector('.price-range__slider')
      if (sliderEl && sliderEl.noUiSlider) {
        window.__mmSuppressPriceRangeChange = true
        try {
          // true fires update (refreshes labels); change does not run on programmatic set.
          sliderEl.noUiSlider.set([minRange, maxRange], true)
        } finally {
          window.__mmSuppressPriceRangeChange = false
        }
      } else if (typeof priceRangeEl.ensureSlider === 'function') {
        requestAnimationFrame(function () {
          priceRangeEl.ensureSlider()
        })
      }

      // Clear again: noUiSlider.set(..., true) update can repopulate hidden inputs before AJAX reads FormData.
      form.querySelectorAll('.price-range input[name]').forEach(function (input) {
        input.value = ''
      })
    })
  }

  function renderGridFromRemoveSearchParams(gridEl, searchParams) {
    if (typeof gridEl.startLoading === 'function') gridEl.startLoading()
    gridEl.isAnimating = true
    if (typeof gridEl.updateScroll === 'function') gridEl.updateScroll(true)
    gridEl.renderCollectionPage(searchParams)
  }

  function renderGridFromRemoveHref(gridEl, href) {
    var removeUrl = new URL(href, window.location.origin)
    renderGridFromRemoveSearchParams(gridEl, removeUrl.searchParams)
  }

  function removeDesktopPriceFilter(gridEl) {
    var cleanParams = syncUrlWithoutPriceParams()
    setPriceInputsDisabled(document, true)
    resetPriceRangeUI(document)

    var priceGroup = document.querySelector('#CollectionSidebarFilterWrap .price-range')
    if (priceGroup) {
      syncFilterCollapsibleForGroup(priceGroup.closest('[data-filter-label]'))
    }

    renderGridFromRemoveSearchParams(gridEl, cleanParams)
  }

  function stripAvailabilityActiveTags(container) {
    var root = container && container.querySelectorAll ? container : document
    root.querySelectorAll('.tag-list--active-tags .tag--remove').forEach(function (tag) {
      if (isAvailabilityActiveTag(tag)) tag.remove()
    })
  }

  function cloneCloseIcon() {
    var existing = document.querySelector('.tag-list--active-tags .tag--remove .icon')
    if (existing) return existing.cloneNode(true)

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.setAttribute('role', 'presentation')
    svg.setAttribute('class', 'icon icon-close')
    svg.setAttribute('viewBox', '0 0 64 64')
    svg.setAttribute('width', '20')
    svg.setAttribute('height', '20')
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', 'm19 17.61 27.12 27.13m0-27.13L19 44.74')
    path.setAttribute('style', 'stroke-miterlimit:10')
    svg.appendChild(path)
    return svg
  }

  /** Detect tags owned by mm-grouped-size-filter — do not handle these here. */
  function isGroupedSizeTag(li) {
    if (!li) return false
    if (li.getAttribute('data-gsz-managed') === 'true') return true
    if (li.hasAttribute('data-gsz-size')) return true
    if (li.hasAttribute('data-dept-tag') && !li.hasAttribute('data-filter-name')) return true
    return false
  }

  function isStandardFilterTag(li) {
    if (!li || !li.classList.contains('tag--remove')) return false
    if (li.classList.contains('hide')) return false
    return !isGroupedSizeTag(li)
  }

  function queryScopeForms(container) {
    if (container && container.querySelectorAll) {
      return container.querySelectorAll('.filter-form')
    }
    return document.querySelectorAll('.filter-form:not([data-mobile-filter])')
  }

  function resolveRemovedFilters(tagEl, removeHref) {
    var paramName = tagEl.getAttribute('data-filter-name')
    var paramValue = tagEl.getAttribute('data-filter-value')

    if (paramName && paramValue) {
      return [{ paramName: paramName, paramValue: paramValue }]
    }

    var removed = []
    var currentParams = new URLSearchParams(window.location.search)
    var removeParams = new URLSearchParams(new URL(removeHref, window.location.origin).search)

    currentParams.forEach(function (value, key) {
      if (!key.startsWith('filter.')) return
      if (!removeParams.getAll(key).includes(value)) {
        removed.push({ paramName: key, paramValue: value })
      }
    })

    return removed
  }

  function buildFilterRemoveHref(paramName, paramValue) {
    var pathname = window.location.pathname
    if (!paramName) return pathname + window.location.search

    var params = new URLSearchParams(window.location.search)
    var kept = params.getAll(paramName).filter(function (value) {
      return value !== paramValue
    })
    params.delete(paramName)
    kept.forEach(function (value) {
      params.append(paramName, value)
    })

    var query = params.toString()
    return query ? pathname + '?' + query : pathname
  }

  function getFilterLabel(input) {
    var li = input.closest('li')
    if (li) {
      var textEl = li.querySelector('.tag__text')
      if (textEl) {
        var label = textEl.textContent.trim()
        if (label) return label
      }
      var swatch = li.querySelector('.color-swatch')
      if (swatch) {
        return (swatch.getAttribute('title') || swatch.textContent || '').trim() || input.value
      }
    }
    return input.value
  }

  function setFilterInputState(paramName, paramValue, isActive, container) {
    queryScopeForms(container).forEach(function (form) {
      form.querySelectorAll('input[name]').forEach(function (input) {
        if (input.classList.contains('grouped-size-input')) return
        if (input.classList.contains('grouped-size-dept-input')) return
        if (input.name !== paramName || input.value !== paramValue) return

        input.checked = isActive
        var parentLi = input.closest('li')
        if (parentLi) parentLi.classList.toggle(ACTIVE_TAG, isActive)
      })
    })
  }

  function tagMatchesFilter(tag, paramName, paramValue) {
    var tagName = tag.getAttribute('data-filter-name')
    var tagValue = tag.getAttribute('data-filter-value')
    if (tagName !== paramName) return false
    if (tagValue === paramValue) return true
    return (
      (tagValue || '').toLowerCase() === (paramValue || '').toLowerCase()
    )
  }

  /**
   * Map a tag label to a checkbox when data-filter-* attrs are missing.
   * Returns null when multiple filters share the same display label (e.g. Color vs Category "Black").
   */
  function findStandardFilterInputForLabel(container, labelLower, urlParams) {
    if (!labelLower) return null
    urlParams = urlParams || new URLSearchParams(window.location.search)

    var scope = container && container.querySelectorAll ? container : document
    var matches = []

    scope.querySelectorAll('.tag__input').forEach(function (input) {
      if (input.classList.contains('grouped-size-input')) return
      if (input.classList.contains('grouped-size-dept-input')) return

      var textEl = input.closest('li') && input.closest('li').querySelector('.tag__text')
      if (!textEl) return
      if (textEl.textContent.trim().toLowerCase() !== labelLower) return
      matches.push(input)
    })

    if (matches.length === 0) return null
    if (matches.length === 1) return matches[0]

    var urlMatches = matches.filter(function (input) {
      return urlParams.getAll(input.name).includes(input.value)
    })
    if (urlMatches.length === 1) return urlMatches[0]

    var checkedMatches = matches.filter(function (input) {
      return input.checked
    })
    if (checkedMatches.length === 1) return checkedMatches[0]

    return null
  }

  function removeActiveTagsForFilter(paramName, paramValue, container, labelHint) {
    var root = container && container.querySelectorAll ? container : document
    var labelLower = (labelHint || '').trim().toLowerCase()

    root.querySelectorAll('.tag-list--active-tags .tag--remove').forEach(function (tag) {
      if (isGroupedSizeTag(tag)) return

      if (tagMatchesFilter(tag, paramName, paramValue)) {
        tag.remove()
        return
      }

      // Legacy tag without data-filter-* — only remove when label maps unambiguously to this param/value.
      if (tag.getAttribute('data-filter-name')) return

      var link = tag.querySelector('a')
      if (!link || !labelLower) return
      if (link.textContent.trim().toLowerCase() !== labelLower) return

      var scope = container && container.querySelectorAll ? container : document
      var form =
        scope.querySelector('.filter-form:not([data-mobile-filter])') ||
        scope.querySelector('.filter-form')
      if (!form) return

      var tiedInputs = []
      form.querySelectorAll('input[name]').forEach(function (input) {
        if (input.name !== paramName) return
        if (
          input.value !== paramValue &&
          input.value.toLowerCase() !== (paramValue || '').toLowerCase()
        ) {
          return
        }
        var textEl = input.closest('li') && input.closest('li').querySelector('.tag__text')
        if (!textEl) return
        if (textEl.textContent.trim().toLowerCase() !== labelLower) return
        tiedInputs.push(input)
      })

      if (tiedInputs.length === 1) tag.remove()
    })
  }

  function getSizeLabel(input) {
    return (input.getAttribute('data-size-label') || '').trim()
  }

  function buildGszFromContainer(container) {
    var gsz = {}
    if (!container || !container.querySelectorAll) return gsz

    container.querySelectorAll('.grouped-size-input').forEach(function (input) {
      if (!input.checked) return
      var group = (input.getAttribute('data-group') || '').trim()
      var subgroup = (input.getAttribute('data-subgroup') || '').trim()
      var size = getSizeLabel(input)
      if (!group || !size) return
      var key = subgroup ? group + '/' + subgroup : group
      if (!gsz[key]) gsz[key] = []
      if (gsz[key].indexOf(size) === -1) gsz[key].push(size)
    })

    return gsz
  }

  function appendGroupedRemoveTag(list, label, extraAttrs) {
    var li = document.createElement('li')
    li.className = 'tag tag--remove'
    li.setAttribute(GSZ_TAG_ATTR, 'true')
    if (extraAttrs) {
      Object.keys(extraAttrs).forEach(function (key) {
        li.setAttribute(key, extraAttrs[key])
      })
    }

    var a = document.createElement('a')
    a.className = 'btn btn--small js-no-transition'
    a.href = '#'
    a.textContent = label
    li.appendChild(a)
    li.appendChild(cloneCloseIcon())
    list.appendChild(li)
  }

  /**
   * Rebuild size + parent-group tags in a filter wrapper (mobile drawer).
   * Mirrors mm-grouped-size-filter syncActiveTagsBar without touching that file.
   */
  function syncGroupedSizeActiveTagsBar(container, gsz) {
    if (!container) return

    gsz = gsz || buildGszFromContainer(container)
    var list = getOrCreateActiveTagsList(container)

    var knownSizeLabels = {}
    container.querySelectorAll('.grouped-size-input[data-size-label]').forEach(function (inp) {
      var lbl = getSizeLabel(inp).toLowerCase()
      if (lbl) knownSizeLabels[lbl] = getSizeLabel(inp)
    })

    var knownGroupNames = {}
    container.querySelectorAll('.grouped-size-filter__group[data-group]').forEach(function (g) {
      var name = g.getAttribute('data-group') || ''
      if (name && name !== 'other') knownGroupNames[name.toLowerCase()] = true
    })

    list.querySelectorAll('.tag--remove').forEach(function (li) {
      if (li.getAttribute(GSZ_TAG_ATTR) === 'true' || li.hasAttribute('data-dept-tag')) {
        li.remove()
        return
      }
      var a = li.querySelector('a')
      if (!a) return
      var tagText = (a.textContent || '').trim().toLowerCase()
      if (knownSizeLabels[tagText] || knownGroupNames[tagText]) li.remove()
    })

    var addedSizes = {}
    var addedDeptGroups = {}
    Object.keys(gsz).forEach(function (group) {
      (gsz[group] || []).forEach(function (size) {
        var key = size.toLowerCase()
        if (addedSizes[key]) return
        addedSizes[key] = true
        var label = knownSizeLabels[key] || size
        appendGroupedRemoveTag(list, label, { 'data-gsz-size': label })
      })
    })

    container.querySelectorAll('.grouped-size-filter__group[data-dept-value][data-group]').forEach(function (g) {
      var groupName = g.getAttribute('data-group') || ''
      var deptValue = g.getAttribute('data-dept-value') || ''
      if (!groupName || !deptValue || groupName === 'other') return
      if (!gsz[groupName] || !gsz[groupName].length) return

      var deptKey = groupName.toLowerCase()
      if (addedDeptGroups[deptKey]) return
      addedDeptGroups[deptKey] = true

      appendGroupedRemoveTag(list, groupName, { 'data-dept-tag': deptValue })
    })
  }

  function removeGroupedSizeFromContainer(container, sizeLabel) {
    if (!container || !sizeLabel) return

    container.querySelectorAll('.grouped-size-input').forEach(function (input) {
      var lbl = getSizeLabel(input)
      if (lbl.toLowerCase() !== sizeLabel.toLowerCase()) return
      input.checked = false
      var parentLi = input.closest('li')
      if (parentLi) parentLi.classList.remove(ACTIVE_TAG)
    })
  }

  function getOrCreateActiveTagsList(wrapper) {
    var lists = wrapper.querySelectorAll('.tag-list--active-tags')
    var list = null

    lists.forEach(function (candidate) {
      if (candidate.parentElement === wrapper) list = candidate
    })
    if (!list && lists.length) list = lists[0]

    lists.forEach(function (candidate) {
      if (candidate !== list) candidate.remove()
    })

    if (!list) {
      list = document.createElement('ul')
      list.className = 'no-bullets tag-list tag-list--active-tags'
      wrapper.insertBefore(list, wrapper.firstChild)
    }

    return list
  }

  function appendStandardFilterTag(wrapper, label, paramName, paramValue) {
    if (isAvailabilityFilter(paramName)) return

    var list = getOrCreateActiveTagsList(wrapper)
    var duplicate = false

    list.querySelectorAll('.tag--remove[data-filter-name]').forEach(function (tag) {
      if (tagMatchesFilter(tag, paramName, paramValue)) duplicate = true
    })
    if (duplicate) return

    var li = document.createElement('li')
    li.className = 'tag tag--remove'
    li.setAttribute(STANDARD_TAG_ATTR, 'true')
    li.setAttribute('data-filter-name', paramName)
    li.setAttribute('data-filter-value', paramValue)

    var a = document.createElement('a')
    a.className = 'btn btn--small js-no-transition'
    a.href = buildFilterRemoveHref(paramName, paramValue)
    a.textContent = label
    li.appendChild(a)
    li.appendChild(cloneCloseIcon())
    list.appendChild(li)
  }

  function syncFilterVisualState(paramName, paramValue, isActive, container) {
    setFilterInputState(paramName, paramValue, isActive, container)

    if (isActive) {
      var label = paramValue
      queryScopeForms(container).forEach(function (form) {
        form.querySelectorAll('input[name]').forEach(function (input) {
          if (input.name === paramName && input.value === paramValue) {
            label = getFilterLabel(input)
          }
        })
      })

      var wrappers = []
      if (container && container.querySelectorAll) {
        wrappers.push(container)
      } else {
        document.querySelectorAll('.filter-wrapper').forEach(function (wrapper) {
          wrappers.push(wrapper)
        })
      }

      wrappers.forEach(function (wrapper) {
        appendStandardFilterTag(wrapper, label, paramName, paramValue)
      })
    } else {
      var labelHint = paramValue
      queryScopeForms(container).forEach(function (form) {
        form.querySelectorAll('input[name]').forEach(function (input) {
          if (input.name === paramName && input.value === paramValue) {
            labelHint = getFilterLabel(input)
          }
        })
      })
      removeActiveTagsForFilter(paramName, paramValue, container, labelHint)
    }

    if (!isActive && isCategoryFilterParam(paramName, container)) {
      syncStyleFilterVisibility(container)
    }
  }

  function hydrateActiveTagAttributes(container) {
    if (!container || !container.querySelectorAll) return

    var urlParams = new URLSearchParams(window.location.search)

    container.querySelectorAll('.tag-list--active-tags .tag--remove').forEach(function (tag) {
      if (isGroupedSizeTag(tag)) return
      if (tag.getAttribute('data-filter-name') && tag.getAttribute('data-filter-value')) return

      var link = tag.querySelector('a')
      if (!link) return

      var label = link.textContent.trim().toLowerCase()
      if (!label) return

      var input = findStandardFilterInputForLabel(container, label, urlParams)
      if (!input) return

      tag.setAttribute('data-filter-name', input.name)
      tag.setAttribute('data-filter-value', input.value)
    })
  }

  function ensureStandardFilterTagIcons(container) {
    stripAvailabilityActiveTags(container)
    if (container) hydrateActiveTagAttributes(container)

    var tags = container && container.querySelectorAll
      ? container.querySelectorAll('.tag-list--active-tags .tag--remove')
      : document.querySelectorAll('.tag-list--active-tags .tag--remove')

    tags.forEach(function (li) {
      if (isPriceActiveTag(li)) li.setAttribute('data-price-managed', 'true')
      if (li.querySelector('.icon')) return
      li.appendChild(cloneCloseIcon())
    })
  }

  function resolveFilterRoot(root) {
    if (root && root.querySelectorAll) return root
    return document
  }

  function getCategoryParamName(root) {
    root = resolveFilterRoot(root)
    var input = root.querySelector('[data-filter-label="Category"] .tag__input[name]')
    return input ? input.name : null
  }

  /** True when param/value belongs to a Category checkbox (not Style — they may share a param name). */
  function isCategoryFilterValue(root, paramName, paramValue) {
    root = resolveFilterRoot(root)
    var categoryGroup = root.querySelector('[data-filter-type="category"]')
    if (!categoryGroup || !paramName || paramValue == null) return false

    var inputs = categoryGroup.querySelectorAll(
      '.tag__input[name="' + CSS.escape(paramName) + '"]'
    )
    var valueLower = String(paramValue).toLowerCase()
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value === paramValue) return true
      if (String(inputs[i].value).toLowerCase() === valueLower) return true
    }
    return false
  }

  /** True when param/value belongs to a Style checkbox only. */
  function isStyleFilterValue(root, paramName, paramValue) {
    root = resolveFilterRoot(root)
    var styleGroup = root.querySelector('[data-filter-type="style"]')
    if (!styleGroup || !paramName || paramValue == null) return false

    var inputs = styleGroup.querySelectorAll(
      '.tag__input[name="' + CSS.escape(paramName) + '"]'
    )
    var valueLower = String(paramValue).toLowerCase()
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value === paramValue) return true
      if (String(inputs[i].value).toLowerCase() === valueLower) return true
    }
    return false
  }

  /**
   * Whether Category is selected in the UI (checked boxes or mobile pending).
   * Does not read the URL — stale params must not keep Style active after uncheck.
   */
  function categoryHasActiveSelection(root, pendingFilters) {
    root = resolveFilterRoot(root)
    var categoryGroup = root.querySelector('[data-filter-type="category"]')
    if (!categoryGroup) return false

    var checkedInputs = categoryGroup.querySelectorAll('.tag__input:checked')
    for (var c = 0; c < checkedInputs.length; c++) {
      if (isCategoryFilterValue(root, checkedInputs[c].name, checkedInputs[c].value)) {
        return true
      }
    }

    if (pendingFilters && typeof pendingFilters.forEach === 'function') {
      var pendingActive = false
      pendingFilters.forEach(function (values, key) {
        if (!values || !values.size) return
        values.forEach(function (value) {
          if (isCategoryFilterValue(root, key, value)) pendingActive = true
        })
      })
      if (pendingActive) return true
    }

    return false
  }

  /**
   * Drop shared Category/Style params when no Category value remains (e.g. filter.p.product_type).
   */
  function sanitizeCategoryStyleSearchParams(searchParams, root) {
    root = resolveFilterRoot(root)
    var paramName = getCategoryParamName(root)
    if (!paramName || !searchParams || !searchParams.has(paramName)) {
      return searchParams
    }

    var sanitized = new URLSearchParams(searchParams)
    var hasCategoryValue = false

    sanitized.getAll(paramName).forEach(function (value) {
      if (isCategoryFilterValue(root, paramName, value)) hasCategoryValue = true
    })

    if (!hasCategoryValue) sanitized.delete(paramName)
    return sanitized
  }

  function isCategoryFilterParam(paramName, root) {
    root = resolveFilterRoot(root)
    var categoryGroup = root.querySelector('[data-filter-type="category"]')
    if (!categoryGroup || !paramName) return false
    return categoryGroup.querySelector('input[name="' + CSS.escape(paramName) + '"]') !== null
  }

  /** Uncheck Style filters and drop pending/tags when Category is no longer active. */
  function clearStyleFilterSelections(root, pendingFilters) {
    root = resolveFilterRoot(root)
    var styleGroups = root.querySelectorAll('[data-filter-type="style"]')
    if (!styleGroups.length) return

    styleGroups.forEach(function (styleGroup) {
      styleGroup.querySelectorAll('.tag__input').forEach(function (input) {
        if (input.classList.contains('grouped-size-input')) return
        if (input.classList.contains('grouped-size-dept-input')) return

        input.checked = false
        var parentLi = input.closest('li')
        if (parentLi) parentLi.classList.remove(ACTIVE_TAG)

        if (pendingFilters && typeof pendingFilters.get === 'function') {
          var pendingValues = pendingFilters.get(input.name)
          if (pendingValues) {
            pendingValues.delete(input.value)
            if (pendingValues.size === 0) pendingFilters.delete(input.name)
          }
        }

        removeActiveTagsForFilter(input.name, input.value, root)
      })
    })
  }

  /** Show Style on shop-all only after Category is selected (toggle .hide). */
  function syncStyleFilterVisibility(root, pendingFilters) {
    root = resolveFilterRoot(root)
    var styleFilters = root.querySelectorAll('[data-filter-type="style"]')
    if (!styleFilters.length) return

    var categoryActive = categoryHasActiveSelection(root, pendingFilters)
    if (!categoryActive) {
      clearStyleFilterSelections(root, pendingFilters)
    }

    styleFilters.forEach(function (styleFilter) {
      styleFilter.classList.toggle('hide', !categoryActive)
      if (!categoryActive) {
        var details = styleFilter.querySelector('.collapsible-content')
        var trigger = styleFilter.querySelector('.collapsible-trigger')
        if (trigger && details) {
          trigger.classList.remove('is-open')
          trigger.setAttribute('aria-expanded', 'false')
          details.classList.remove('is-open')
          details.style.height = '0px'
        }
      }
    })
  }

  /** Category params for section-render fetch (URL + pending + checked). */
  function collectCategoryOnlyParams(root, pendingFilters) {
    root = resolveFilterRoot(root)
    var paramName = getCategoryParamName(root)
    var params = new URLSearchParams()
    if (!paramName) return params

    var values = new Set()

    if (pendingFilters && typeof pendingFilters.forEach === 'function') {
      pendingFilters.forEach(function (pendingValues, key) {
        if (key !== paramName || !pendingValues) return
        pendingValues.forEach(function (value) {
          if (value && isCategoryFilterValue(root, key, value)) values.add(value)
        })
      })
    }

    root.querySelectorAll('[data-filter-label="Category"] .tag__input:checked').forEach(function (input) {
      if (input.value && isCategoryFilterValue(root, input.name, input.value)) {
        values.add(input.value)
      }
    })

    values.forEach(function (value) {
      params.append(paramName, value)
    })
    return params
  }

  function getFilterSectionId() {
    var itemGrid = document.querySelector('item-grid[data-section-id]')
    return itemGrid ? itemGrid.getAttribute('data-section-id') : 'item-grid-filters'
  }

  function filterValueIsActive(paramName, paramValue, urlParams, pendingFilters) {
    var activeValues = urlParams.getAll(paramName)
    var i
    for (i = 0; i < activeValues.length; i++) {
      if (activeValues[i] === paramValue) return true
      if (
        (activeValues[i] || '').toLowerCase() === (paramValue || '').toLowerCase()
      ) {
        return true
      }
    }

    if (!pendingFilters || typeof pendingFilters.get !== 'function') return false
    var pendingValues = pendingFilters.get(paramName)
    if (!pendingValues || !pendingValues.forEach) return false

    var found = false
    pendingValues.forEach(function (pendingValue) {
      if (pendingValue === paramValue) found = true
      if (
        (pendingValue || '').toLowerCase() === (paramValue || '').toLowerCase()
      ) {
        found = true
      }
    })
    return found
  }

  /** Restore standard filter checkbox state after Style HTML refresh (sidebar is preserved on AJAX). */
  function syncStandardFilterCheckboxesFromUrl(root, pendingFilters) {
    root = resolveFilterRoot(root)
    var urlParams = new URLSearchParams(window.location.search)
    var categoryActive = categoryHasActiveSelection(root, pendingFilters)

    queryScopeForms(root).forEach(function (form) {
      form.querySelectorAll('.tag__input').forEach(function (input) {
        if (input.classList.contains('grouped-size-input')) return
        if (input.classList.contains('grouped-size-dept-input')) return
        if (input.classList.contains('grouped-size-gender-input')) return
        if (isAvailabilityFilter(input.name)) return
        if (input.name && input.name.indexOf('custom.department') !== -1) return

        var parentLi = input.closest('li')
        if (!categoryActive && isStyleFilterValue(root, input.name, input.value)) {
          input.checked = false
          if (parentLi) parentLi.classList.remove(ACTIVE_TAG)
          return
        }

        var shouldCheck = filterValueIsActive(
          input.name,
          input.value,
          urlParams,
          pendingFilters
        )
        input.checked = shouldCheck
        if (parentLi) parentLi.classList.toggle(ACTIVE_TAG, shouldCheck)
      })
    })
  }

  /**
   * Replace Style option list from a category-scoped section render.
   * Needed because item-grid AJAX preserves the sidebar DOM.
   */
  function refreshStyleFilterOptions(root, pendingFilters) {
    root = resolveFilterRoot(root)
    if (!categoryHasActiveSelection(root, pendingFilters)) {
      refreshStyleRequestId += 1
      syncStyleFilterVisibility(root, pendingFilters)
      return Promise.resolve()
    }

    var categoryParams = collectCategoryOnlyParams(root, pendingFilters)
    if (!categoryParams.toString()) {
      refreshStyleRequestId += 1
      syncStyleFilterVisibility(root, pendingFilters)
      return Promise.resolve()
    }

    var requestId = ++refreshStyleRequestId

    var sectionId = getFilterSectionId()
    var fetchUrl =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      '&' +
      categoryParams.toString()

    return fetch(fetchUrl)
      .then(function (response) {
        if (requestId !== refreshStyleRequestId) return null
        if (!response.ok) throw new Error('Style filter section fetch failed: ' + response.status)
        return response.text()
      })
      .then(function (html) {
        if (requestId !== refreshStyleRequestId || html == null) return

        var doc = new DOMParser().parseFromString(html, 'text/html')
        var sourceStyle = doc.querySelector('[data-filter-label="Style"]')
        if (!sourceStyle) return

        root.querySelectorAll('[data-filter-label="Style"]').forEach(function (targetStyle) {
          var targetInner = targetStyle.querySelector('.collapsible-content__inner')
          var sourceInner = sourceStyle.querySelector('.collapsible-content__inner')
          if (
            targetInner &&
            sourceInner &&
            targetInner.innerHTML !== sourceInner.innerHTML
          ) {
            targetInner.innerHTML = sourceInner.innerHTML
          }
        })

        syncStyleFilterVisibility(root, pendingFilters)
        syncStandardFilterCheckboxesFromUrl(root, pendingFilters)
      })
      .catch(function (err) {
        if (requestId !== refreshStyleRequestId) return
        console.warn('[mm-filter-sync] refreshStyleFilterOptions:', err)
        syncStyleFilterVisibility(root, pendingFilters)
        syncStandardFilterCheckboxesFromUrl(root, pendingFilters)
      })
  }

  function getDesktopFilterWrapper(root) {
    root = resolveFilterRoot(root)
    return (
      root.querySelector('#CollectionSidebarFilterWrap .filter-wrapper') ||
      document.querySelector('#CollectionSidebarFilterWrap .filter-wrapper')
    )
  }

  function getCollectionPath() {
    return window.location.pathname
  }

  function getPreservedUrlParams() {
    var params = new URLSearchParams()
    var currentParams = new URLSearchParams(window.location.search)
    var preservedKeys = ['sort_by', 'q', 'options[prefix]', 'type']
    preservedKeys.forEach(function (key) {
      var value = currentParams.get(key)
      if (value) params.set(key, value)
    })
    // In-stock is on every collection URL here (mm-theme.js); the "full" Size block must be
    // fetched with it or its counts include sold-out stock (468 vs 346 for the same size).
    var availability = currentParams.get(AVAILABILITY_PARAM)
    if (availability) params.set(AVAILABILITY_PARAM, availability)
    return params
  }

  /** Sort/search/type + in-stock — same inputs as desktop Size section fetches. */
  function getSizeFetchCacheContextKey() {
    var params = getPreservedUrlParams()
    var availability = new URLSearchParams(window.location.search).get(AVAILABILITY_PARAM)
    if (availability) params.set(AVAILABILITY_PARAM, availability)
    return params.toString() || '_'
  }

  function buildFullSizeCacheKey() {
    return getCollectionPath() + '|unfiltered|' + getSizeFetchCacheContextKey()
  }

  function buildSizeScopeCacheKey(scopeParams) {
    return (
      getCollectionPath() +
      '|size-scope:' +
      scopeParams.toString() +
      '|ctx:' +
      getSizeFetchCacheContextKey()
    )
  }

  function clearDesktopSizeFilterCaches() {
    fullSizeFilterCache.clear()
    filterWrapperSectionCache.clear()
  }

  function countSizeGroups(sizeBlock) {
    if (!sizeBlock) return 0
    return sizeBlock.querySelectorAll(
      '.grouped-size-filter__group[data-dept-valid="true"]'
    ).length
  }

  function cacheFullSizeBlock(sizeBlock, cacheKey) {
    if (!sizeBlock || !sizeBlock.querySelector('.grouped-size-filter')) return

    cacheKey = cacheKey || buildFullSizeCacheKey()
    var count = countSizeGroups(sizeBlock)
    if (!count) return

    var cached = fullSizeFilterCache.get(cacheKey)
    if (!cached || count > cached.count) {
      fullSizeFilterCache.set(cacheKey, {
        count: count,
        html: sizeBlock.cloneNode(true)
      })
    }
  }

  function sizeDepartmentKey(sizeBlock) {
    if (!sizeBlock) return ''
    var groups = sizeBlock.querySelectorAll('.grouped-size-filter__group[data-group]')
    var names = []
    groups.forEach(function (groupEl) {
      var name = (groupEl.getAttribute('data-group') || '').trim()
      if (name) names.push(name)
    })
    return names.join('|')
  }

  function copySizeCollapsibleState(fromBlock, toBlock) {
    var fromTrigger = fromBlock.querySelector('.collapsible-trigger')
    var toTrigger = toBlock.querySelector('.collapsible-trigger')
    var fromContent = fromBlock.querySelector('.collapsible-content')
    var toContent = toBlock.querySelector('.collapsible-content')
    if (!fromTrigger || !toTrigger || !fromContent || !toContent) return

    var open = fromTrigger.classList.contains('is-open')
    toTrigger.classList.toggle('is-open', open)
    toTrigger.setAttribute('aria-expanded', open ? 'true' : 'false')
    toContent.classList.toggle('is-open', open)
    toContent.style.height = open ? 'auto' : '0px'
  }

  function getSizeDepartmentScopeParams(root) {
    var params = collectCategoryOnlyParams(root)
    if (!params.toString()) return params

    var availability = new URLSearchParams(window.location.search).get(AVAILABILITY_PARAM)
    if (availability && !params.has(AVAILABILITY_PARAM)) {
      params.append(AVAILABILITY_PARAM, availability)
    }
    return params
  }

  function desktopSizeScopeKey(root) {
    var ctx = getSizeFetchCacheContextKey()
    var categoryKey = collectCategoryOnlyParams(root).toString()
    return categoryKey ? 'category:' + categoryKey + '|' + ctx : 'all|' + ctx
  }

  function fetchDesktopFilterWrapperSection(cacheKey, extraParams) {
    if (cacheKey) {
      var cached = filterWrapperSectionCache.get(cacheKey)
      if (cached) return Promise.resolve(cached.cloneNode(true))
    }

    // Full Size restore (|unfiltered| key, no extraParams) always section-fetches — never clone live sidebar.

    var params = getPreservedUrlParams()
    if (extraParams) {
      extraParams.forEach(function (value, key) {
        if (params.getAll(key).indexOf(value) === -1) params.append(key, value)
      })
    }

    var sectionId = getFilterSectionId()
    var fetchUrl =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      (params.toString() ? '&' + params.toString() : '')

    return fetch(fetchUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('Size scope section fetch failed: ' + response.status)
        return response.text()
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html')
        var wrapper =
          doc.querySelector('#CollectionSidebarFilterWrap .filter-wrapper') ||
          doc.querySelector('.filter-wrapper')
        if (!wrapper) throw new Error('Size scope fetch: filter-wrapper not found')

        var stored = wrapper.cloneNode(true)
        if (cacheKey) {
          filterWrapperSectionCache.set(cacheKey, stored)
          var sizeBlock = stored.querySelector('[data-filter-label="Size"]')
          if (sizeBlock && cacheKey.indexOf('|unfiltered|') !== -1) {
            cacheFullSizeBlock(sizeBlock, cacheKey)
          }
        }
        return stored.cloneNode(true)
      })
      .catch(function (err) {
        console.warn('[mm-filter-sync] fetchDesktopFilterWrapperSection:', err)
        return null
      })
  }

  function fetchFullDesktopSizeFilterBlock() {
    var cacheKey = buildFullSizeCacheKey()
    return fetchDesktopFilterWrapperSection(cacheKey, null).then(function (fullWrapper) {
      if (!fullWrapper) return null
      var sizeBlock = fullWrapper.querySelector('[data-filter-label="Size"]')
      if (!sizeBlock || !sizeBlock.querySelector('.grouped-size-filter')) return null
      cacheFullSizeBlock(sizeBlock, cacheKey)
      return sizeBlock.cloneNode(true)
    })
  }

  function pickFullestSizeBlock(candidates) {
    var best = null
    var bestCount = 0
    candidates.forEach(function (block) {
      if (!block) return
      var count = countSizeGroups(block)
      if (count > bestCount) {
        best = block
        bestCount = count
      }
    })
    return best ? { block: best, count: bestCount } : null
  }

  function applyGszCheckboxesToContainer(container, gsz) {
    if (!container) return
    gsz = gsz || {}

    container.querySelectorAll('.grouped-size-input').forEach(function (input) {
      var group = (input.getAttribute('data-group') || '').trim()
      var subgroup = (input.getAttribute('data-subgroup') || '').trim()
      var key = subgroup ? group + '/' + subgroup : group
      var activeSizes = (gsz[key] || []).map(function (size) {
        return size.toLowerCase()
      })
      var sizeLabel = (input.getAttribute('data-size-label') || '').trim().toLowerCase()
      var checked = sizeLabel ? activeSizes.indexOf(sizeLabel) !== -1 : false
      input.checked = checked
      var parentLi = input.closest('li')
      if (parentLi) parentLi.classList.toggle(ACTIVE_TAG, checked)
    })
  }

  function countCheckedGroupedSizes(container) {
    if (!container) return 0
    return container.querySelectorAll('.grouped-size-input:checked').length
  }

  function restoreDesktopGroupedSizeAfterBlockSwap(root, gsz, categoryScoped) {
    applyGszCheckboxesToContainer(root, gsz)
    if (typeof window.__groupedSizeSyncDeptInputs === 'function') {
      window.__groupedSizeSyncDeptInputs(true)
    }
    if (typeof window.__groupedSizeUpdateGsz === 'function') {
      window.__groupedSizeUpdateGsz(root)
    }
    syncGroupedSizeActiveTagsBar(root, gsz)
    syncFilterCollapsibleForGroup(root.querySelector('[data-filter-label="Size"]'))
    if (categoryScoped && typeof window.__groupedSizeHideZeroCounts === 'function') {
      window.__groupedSizeHideZeroCounts(root)
    }
  }

  function submitDesktopFilterFormFromSidebar(root) {
    var gridEl = getGridElement()
    var form = root.querySelector('.filter-form:not([data-mobile-filter])')
    if (!gridEl || !form || typeof gridEl.renderFromFormData !== 'function') return false

    document.dispatchEvent(new Event('filter:selected'))
    if (typeof gridEl.startLoading === 'function') gridEl.startLoading()
    gridEl.isAnimating = true
    gridEl.renderFromFormData(new FormData(form))
    return true
  }

  function flushPendingGridReloadAfterSizeScope() {
    if (!pendingGridReloadAfterSizeScope) return

    var filterWrapper = getDesktopFilterWrapper()
    if (!filterWrapper) {
      pendingGridReloadAfterSizeScope = false
      return
    }

    var gridEl = getGridElement()
    if (!gridEl) {
      pendingGridReloadAfterSizeScope = false
      return false
    }
    if (gridEl.isAnimating) return false

    if (submitDesktopFilterFormFromSidebar(filterWrapper)) {
      pendingGridReloadAfterSizeScope = false
      return true
    }

    pendingGridReloadAfterSizeScope = false
    console.debug('[mm-filter-sync] pending grid reload skipped (no desktop filter form)')
    return false
  }

  /**
   * collection:reloaded runs while item-grid isAnimating is still true (.finally clears it later).
   * Poll until the grid is idle, then submit the pruned filter form.
   */
  function scheduleFlushPendingGridReloadAfterSizeScope() {
    if (!pendingGridReloadAfterSizeScope) return
    if (pendingGridReloadFlushTimer) return

    var attempts = 0
    function tryFlush() {
      pendingGridReloadFlushTimer = null
      if (!pendingGridReloadAfterSizeScope) return

      var gridEl = getGridElement()
      if (!gridEl) {
        pendingGridReloadAfterSizeScope = false
        return
      }

      if (gridEl.isAnimating) {
        attempts += 1
        if (attempts >= PENDING_GRID_RELOAD_MAX_ATTEMPTS) {
          pendingGridReloadAfterSizeScope = false
          console.warn(
            '[mm-filter-sync] pending grid reload timed out waiting for item-grid isAnimating'
          )
          return
        }
        pendingGridReloadFlushTimer = setTimeout(tryFlush, PENDING_GRID_RELOAD_POLL_MS)
        return
      }

      flushPendingGridReloadAfterSizeScope()
    }

    pendingGridReloadFlushTimer = setTimeout(tryFlush, 0)
  }

  function maybeReloadGridAfterSizeScopeChange(root, checkedBefore) {
    // Count-only check: swap that replaces one checked size with another would not reload (rare).
    var checkedAfter = countCheckedGroupedSizes(root)
    if (checkedBefore === checkedAfter) return

    var gridEl = getGridElement()
    if (!gridEl || typeof gridEl.renderFromFormData !== 'function') return

    if (gridEl.isAnimating) {
      pendingGridReloadAfterSizeScope = true
      scheduleFlushPendingGridReloadAfterSizeScope()
      return
    }

    submitDesktopFilterFormFromSidebar(root)
  }

  function syncCategoryScopedDesktopSizeFilter(root, scopeParams, scopeKey, request) {
    var cacheKey = buildSizeScopeCacheKey(scopeParams)
    return fetchDesktopFilterWrapperSection(cacheKey, scopeParams).then(function (scopedWrapper) {
      if (request !== sizeScopeRequestId) return false

      var filterWrapper = getDesktopFilterWrapper(root)
      if (!filterWrapper) return false

      var sizeBlock = filterWrapper.querySelector('[data-filter-label="Size"]')
      if (!sizeBlock || !sizeBlock.querySelector('.grouped-size-filter')) return false

      var scopedSize =
        scopedWrapper && scopedWrapper.querySelector('[data-filter-label="Size"]')
      if (!scopedSize || !scopedSize.querySelector('.grouped-size-filter')) {
        console.warn(
          '[mm-filter-sync] category-scoped Size fetch missing grouped filter; keeping current Size block',
          scopeParams.toString()
        )
        return false
      }

      if (sizeDepartmentKey(sizeBlock) === sizeDepartmentKey(scopedSize)) {
        sizeBlock.setAttribute('data-size-scope', scopeKey)
        return false
      }

      var checkedBefore = countCheckedGroupedSizes(filterWrapper)
      var gsz = buildGszFromContainer(filterWrapper)
      var replacement = scopedSize.cloneNode(true)
      replacement.setAttribute('data-size-scope', scopeKey)
      copySizeCollapsibleState(sizeBlock, replacement)
      sizeBlock.replaceWith(replacement)
      restoreDesktopGroupedSizeAfterBlockSwap(filterWrapper, gsz, true)
      maybeReloadGridAfterSizeScopeChange(filterWrapper, checkedBefore)
      return true
    })
  }

  function syncFullDesktopGroupedSizeFilter(root) {
    root = resolveFilterRoot(root)
    if (!isDesktopSidebarContext()) return Promise.resolve(false)

    var filterWrapper = getDesktopFilterWrapper(root)
    if (!filterWrapper) return Promise.resolve(false)

    var sizeBlock = filterWrapper.querySelector('[data-filter-label="Size"]')
    if (!sizeBlock || !sizeBlock.querySelector('.grouped-size-filter')) {
      return Promise.resolve(false)
    }

    var request = ++sizeScopeRequestId
    var scopeKey = desktopSizeScopeKey(filterWrapper)
    if (sizeBlock.getAttribute('data-size-scope') === scopeKey) {
      return Promise.resolve(false)
    }

    var scopeParams = getSizeDepartmentScopeParams(filterWrapper)
    var categoryScoped = Boolean(scopeParams.toString())

    if (categoryScoped) {
      return syncCategoryScopedDesktopSizeFilter(
        filterWrapper,
        scopeParams,
        scopeKey,
        request
      )
    }

    var gsz = buildGszFromContainer(filterWrapper)
    var fullSizeCacheKey = buildFullSizeCacheKey()
    var desktopCount = countSizeGroups(sizeBlock)
    var cached = fullSizeFilterCache.get(fullSizeCacheKey)

    if (desktopCount > 0) cacheFullSizeBlock(sizeBlock, fullSizeCacheKey)

    // Desktop sidebar only — never clone mobile drawer Size markup (duplicate ids / names).
    var picked = pickFullestSizeBlock([cached && cached.html, sizeBlock])

    var fetchPromise = Promise.resolve(null)
    if (!picked || picked.count <= desktopCount) {
      fetchPromise = fetchFullDesktopSizeFilterBlock()
    }

    return fetchPromise.then(function (fetched) {
      if (request !== sizeScopeRequestId) return false

      picked = pickFullestSizeBlock([picked && picked.block, fetched, sizeBlock])
      sizeBlock = filterWrapper.querySelector('[data-filter-label="Size"]')
      if (!sizeBlock || !sizeBlock.querySelector('.grouped-size-filter')) return false

      if (!picked || picked.count <= desktopCount) {
        sizeBlock.setAttribute('data-size-scope', scopeKey)
        return false
      }

      var checkedBefore = countCheckedGroupedSizes(filterWrapper)
      var replacement = picked.block.cloneNode(true)
      replacement.setAttribute('data-size-scope', scopeKey)
      copySizeCollapsibleState(sizeBlock, replacement)
      sizeBlock.replaceWith(replacement)

      if (typeof window.__groupedSizeSyncFilterCounts === 'function') {
        window.__groupedSizeSyncFilterCounts(filterWrapper, filterWrapper)
      }

      restoreDesktopGroupedSizeAfterBlockSwap(filterWrapper, gsz, false)
      maybeReloadGridAfterSizeScopeChange(filterWrapper, checkedBefore)
      return true
    })
  }

  /** @type {Map<string, number>} Latest dept-group count fetch id per data-dept-value */
  var deptGroupCountRequestByDept = new Map()

  /**
   * Department-scoped renders, kept for the life of the page. Their inputs are the
   * category selection, in-stock and one department — never the selected sizes — so
   * a grid reload after a size click reuses them instead of re-fetching (and greying)
   * every open group. An entry holds the in-flight promise so concurrent callers
   * share one request; `el` is set once the render arrived. Read-only: callers never
   * mutate the stored wrapper.
   * @type {Map<string, {promise: Promise<Element|null>, el: Element|null}>}
   */
  var deptGroupSectionCache = new Map()
  var DEPT_GROUP_SECTION_CACHE_MAX = 24

  function fetchDepartmentGroupSection(cacheKey, scopeParams) {
    var entry = deptGroupSectionCache.get(cacheKey)
    if (!entry) {
      entry = { promise: null, el: null }
      var forget = function () {
        // Failed (null result, or a rejection should the helper ever throw): drop the
        // entry so the next open retries instead of replaying the failure.
        if (deptGroupSectionCache.get(cacheKey) === entry) deptGroupSectionCache.delete(cacheKey)
        return null
      }
      // cacheKey null on purpose: these renders live in deptGroupSectionCache, which
      // onCollectionReloaded does not wipe, instead of filterWrapperSectionCache.
      entry.promise = fetchDesktopFilterWrapperSection(null, scopeParams)
        .then(function (wrapper) {
          if (!wrapper) return forget()
          entry.el = wrapper
          return wrapper
        })
        .catch(forget)
      deptGroupSectionCache.set(cacheKey, entry)
      while (deptGroupSectionCache.size > DEPT_GROUP_SECTION_CACHE_MAX) {
        deptGroupSectionCache.delete(deptGroupSectionCache.keys().next().value)
      }
    }
    return entry.promise
  }

  /**
   * Start (or join) the department render for one group without touching its UI.
   * Honours Save-Data for every warm-up path (prefetch and hover).
   */
  function warmDepartmentGroupSection(filterWrapper, groupEl) {
    if (!filterWrapper || !groupEl || groupEl.classList.contains('hide')) return null
    if (navigator.connection && navigator.connection.saveData) return null
    var groupName = (groupEl.getAttribute('data-group') || '').trim()
    var deptValue = (groupEl.getAttribute('data-dept-value') || '').trim()
    if (!groupName || groupName === 'other' || !deptValue) return null
    var cacheKey = buildDepartmentGroupCountCacheKey(filterWrapper, deptValue)
    return fetchDepartmentGroupSection(cacheKey, getDepartmentGroupScopeParams(filterWrapper, deptValue))
  }

  /**
   * Warm the department-render cache for every size group so the first open of a group
   * finds its counts ready instead of showing rows without numbers for 1–2 s. One
   * Shopify render at a time, in group order; a group the shopper opens meanwhile fetches
   * on its own and shares the cache. Skipped when the browser asks for reduced data.
   * Runs once the Size filter is open (page load, Size trigger click, grid reload); a
   * second call while the first is running is a no-op, and cache hits cost nothing.
   */
  var deptGroupPrefetchRunning = false
  function prefetchDepartmentGroupSections(root) {
    if (deptGroupPrefetchRunning || !isDesktopSidebarContext()) return
    var filterWrapper = getDesktopFilterWrapper(root)
    if (!filterWrapper) return
    var sizeGroup = filterWrapper.querySelector('[data-filter-label="Size"]')
    if (!sizeGroup || !sizeGroup.querySelector('.collapsible-trigger.is-open')) return
    var groups = Array.prototype.slice.call(
      sizeGroup.querySelectorAll('.grouped-size-filter__group[data-dept-value]')
    )
    if (!groups.length) return

    deptGroupPrefetchRunning = true
    var chain = Promise.resolve()
    groups.forEach(function (groupEl) {
      chain = chain.then(function () {
        return warmDepartmentGroupSection(getDesktopFilterWrapper(root) || filterWrapper, groupEl)
      })
    })
    chain.catch(function () {}).then(function () {
      deptGroupPrefetchRunning = false
    })
  }

  function scheduleDepartmentGroupPrefetch(root) {
    var run = function () {
      prefetchDepartmentGroupSections(root)
    }
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 2000 })
    } else {
      setTimeout(run, 600)
    }
  }

  function buildDepartmentGroupCountCacheKey(root, deptValue) {
    var categoryKey = collectCategoryOnlyParams(root).toString() || '_'
    return (
      getCollectionPath() +
      '|dept-group:' +
      categoryKey +
      '|' +
      encodeURIComponent(deptValue) +
      '|ctx:' +
      getSizeFetchCacheContextKey()
    )
  }

  function getDepartmentGroupScopeParams(root, deptValue) {
    var params = getSizeDepartmentScopeParams(root)
    if (!params.toString()) {
      params = new URLSearchParams()
      var availability = new URLSearchParams(window.location.search).get(AVAILABILITY_PARAM)
      if (availability) params.set(AVAILABILITY_PARAM, availability)
    }
    if (deptValue && !params.getAll(DEPT_PARAM).includes(deptValue)) {
      params.append(DEPT_PARAM, deptValue)
    }
    return params
  }

  function syncDesktopDepartmentGroupCounts(root, groupEl) {
    if (!root || !groupEl) return Promise.resolve()

    var groupName = (groupEl.getAttribute('data-group') || '').trim()
    var deptValue = (groupEl.getAttribute('data-dept-value') || '').trim()
    if (!groupName || groupName === 'other' || !deptValue) return Promise.resolve()

    var cacheKey = buildDepartmentGroupCountCacheKey(root, deptValue)
    var scopeParams = getDepartmentGroupScopeParams(root, deptValue)
    var request = (deptGroupCountRequestByDept.get(deptValue) || 0) + 1
    deptGroupCountRequestByDept.set(deptValue, request)

    var ready = deptGroupSectionCache.get(cacheKey)
    if (ready && ready.el) {
      // Render already in hand (earlier open, or a reload after a size click): apply at
      // once, with no pending state.
      groupEl.classList.remove('grouped-size-filter__group--counts-pending')
      groupEl.removeAttribute('aria-busy')
      if (typeof window.__groupedSizeSyncFilterCountsForGroup === 'function') {
        window.__groupedSizeSyncFilterCountsForGroup(ready.el, root, groupName)
      }
      return Promise.resolve()
    }

    groupEl.classList.add('grouped-size-filter__group--counts-pending')
    groupEl.setAttribute('aria-busy', 'true')

    return fetchDepartmentGroupSection(cacheKey, scopeParams)
      .then(function (scopedWrapper) {
        if (deptGroupCountRequestByDept.get(deptValue) !== request) return
        if (!scopedWrapper || typeof window.__groupedSizeSyncFilterCountsForGroup !== 'function') {
          return
        }
        window.__groupedSizeSyncFilterCountsForGroup(scopedWrapper, root, groupName)
      })
      .finally(function () {
        if (deptGroupCountRequestByDept.get(deptValue) === request) {
          groupEl.classList.remove('grouped-size-filter__group--counts-pending')
          groupEl.removeAttribute('aria-busy')
        }
      })
  }

  function refreshOpenDesktopDepartmentGroupCounts(root) {
    if (!isDesktopSidebarContext()) return Promise.resolve()

    var filterWrapper = getDesktopFilterWrapper(root)
    if (!filterWrapper) return Promise.resolve()

    var openDetails = filterWrapper.querySelectorAll(
      '.grouped-size-filter__group-details[open]'
    )
    var chain = Promise.resolve()
    openDetails.forEach(function (details) {
      var groupEl = details.closest('.grouped-size-filter__group[data-dept-value]')
      if (!groupEl) return
      chain = chain.then(function () {
        return syncDesktopDepartmentGroupCounts(filterWrapper, groupEl)
      })
    })
    return chain
  }

  function refreshDesktopSizeGroupsAfterCategoryChange(root) {
    if (!isDesktopSidebarContext()) return Promise.resolve()
    return syncFullDesktopGroupedSizeFilter(root)
      .then(function () {
        return refreshOpenDesktopDepartmentGroupCounts(root)
      })
      .catch(function (err) {
        console.warn('[mm-filter-sync] refreshDesktopSizeGroupsAfterCategoryChange:', err)
      })
  }

  function scheduleRefreshDesktopSizeGroupsAfterCategoryChange(root) {
    if (!isDesktopSidebarContext()) return

    if (sizeScopeDebounceTimer) clearTimeout(sizeScopeDebounceTimer)
    sizeScopeDebounceTimer = setTimeout(function () {
      sizeScopeDebounceTimer = null
      var sidebar = document.querySelector('#CollectionSidebarFilterWrap')
      refreshDesktopSizeGroupsAfterCategoryChange(sidebar || root || document)
    }, SIZE_SCOPE_DEBOUNCE_MS)
  }

  function getGridElement() {
    return document.querySelector('item-grid') || document.querySelector('collection-template')
  }

  // Desktop sidebar is visible at min-width: 769px — Archetype theme mobile breakpoint
  // (CSS uses max-width: 768px; see snippets/item-grid-controls.liquid, assets/mm-styles.css).
  function isDesktopSidebarContext() {
    return window.matchMedia('(min-width: 769px)').matches
  }

  function filterGroupHasActiveSelection(groupEl) {
    if (!groupEl) return false

    var label = groupEl.getAttribute('data-filter-label') || ''
    if (!label || label === 'Availability' || label === 'Product Department') return false

    if (label === 'Size' && groupEl.querySelector('.grouped-size-filter')) {
      return groupEl.querySelector('.grouped-size-input:checked') !== null
    }

    if (groupEl.querySelector('.price-range')) {
      var form = groupEl.closest('.filter-form')
      var minInput = groupEl.querySelector('.price-range__input-min')
      var maxInput = groupEl.querySelector('.price-range__input-max')
      var minVal = minInput && String(minInput.value).trim()
      var maxVal = maxInput && String(maxInput.value).trim()
      return isPriceSelectionActive(minVal, maxVal, getPriceRangeBounds(form))
    }

    return (
      groupEl.querySelector(
        '.tag__input:checked:not(.grouped-size-dept-input):not(.grouped-size-input)'
      ) !== null
    )
  }

  function setFilterCollapsibleOpen(groupEl, shouldOpen) {
    var trigger = groupEl.querySelector('.collapsible-trigger')
    var content = groupEl.querySelector('.collapsible-content')
    if (!trigger || !content) return

    var isOpen = trigger.classList.contains('is-open')
    if (shouldOpen === isOpen) return

    if (shouldOpen) {
      trigger.classList.add('is-open')
      trigger.setAttribute('aria-expanded', 'true')
      content.classList.add('is-open')
      content.style.height = 'auto'
      refreshPriceRangeInGroup(groupEl)
      return
    }

    trigger.classList.remove('is-open')
    trigger.setAttribute('aria-expanded', 'false')
    content.classList.remove('is-open')
    content.style.height = '0px'
  }

  function refreshPriceRangeInGroup(groupEl) {
    if (!groupEl) return
    var priceRangeEl = groupEl.querySelector('price-range.price-range')
    if (!priceRangeEl || typeof priceRangeEl.ensureSlider !== 'function') return
    requestAnimationFrame(function () {
      priceRangeEl.ensureSlider()
    })
  }

  function collapseFiltersEnabled(groupEl) {
    var wrapper =
      groupEl.closest('.filter-wrapper') ||
      groupEl.closest('#CollectionSidebarFilterWrap') ||
      document.querySelector('#CollectionSidebarFilterWrap')

    var form = wrapper
      ? wrapper.querySelector('.filter-form:not([data-mobile-filter])')
      : document.querySelector('#CollectionSidebarFilterWrap .filter-form:not([data-mobile-filter])')

    if (!form) return true
    return form.getAttribute('data-collapse-filters') !== 'false'
  }

  function shouldFilterGroupBeOpen(groupEl) {
    if (filterGroupHasActiveSelection(groupEl)) return true
    if (!collapseFiltersEnabled(groupEl)) {
      var label = groupEl.getAttribute('data-filter-label') || ''
      if (label === 'Availability' || label === 'Product Department') return false
      if (groupEl.classList.contains('hide')) return false
      return true
    }
    return false
  }

  function syncFilterCollapsibleForGroup(groupEl) {
    if (!groupEl || !isDesktopSidebarContext()) return
    if (groupEl.closest('collection-mobile-filters')) return
    setFilterCollapsibleOpen(groupEl, shouldFilterGroupBeOpen(groupEl))
  }

  function syncDesktopFilterCollapsibles(root) {
    if (!isDesktopSidebarContext()) return

    root = root || document
    var sidebar = root.querySelector('#CollectionSidebarFilterWrap')
    if (!sidebar) return

    sidebar.querySelectorAll('[data-filter-label]').forEach(syncFilterCollapsibleForGroup)
  }

  // No filter UI on this page — skip listeners and window exports.
  if (!document.querySelector('.filter-form')) {
    return
  }

  // Desktop: active-tag × click — capture before theme handler (same pattern as grouped size).
  document.addEventListener(
    'click',
    function (e) {
      var li = e.target.closest('.tag-list--active-tags .tag--remove')
      if (!li || !isStandardFilterTag(li)) return
      if (li.closest('collection-mobile-filters')) return
      if (isAvailabilityActiveTag(li)) return

      var formEl = li.closest('form')
      if (formEl && formEl.hasAttribute('data-mobile-filter')) return

      var link = li.querySelector('a')
      if (!link || link.classList.contains('no-ajax')) return

      var gridEl = getGridElement()
      if (!gridEl) return

      if (gridEl.isAnimating) return

      e.preventDefault()
      e.stopImmediatePropagation()

      document.dispatchEvent(new Event('filter:selected'))

      if (isPriceActiveTag(li)) {
        li.remove()
        removeDesktopPriceFilter(gridEl)
        return
      }

      if (!li.getAttribute('data-filter-name') && !li.hasAttribute('data-dept-tag')) {
        return
      }

      var sidebar = document.querySelector('#CollectionSidebarFilterWrap')
      var removedCategory = false
      resolveRemovedFilters(li, link.href).forEach(function (filter) {
        syncFilterVisualState(filter.paramName, filter.paramValue, false, document)
        if (
          sidebar &&
          isCategoryFilterValue(sidebar, filter.paramName, filter.paramValue)
        ) {
          removedCategory = true
        }
      })
      li.remove()

      if (removedCategory && sidebar) {
        syncStyleFilterVisibility(sidebar)
        scheduleRefreshDesktopSizeGroupsAfterCategoryChange(sidebar)
      }

      var form = document.querySelector('.filter-form:not([data-mobile-filter])')
      if (!form) return

      if (typeof gridEl.startLoading === 'function') gridEl.startLoading()
      gridEl.isAnimating = true
      gridEl.renderFromFormData(new FormData(form))
    },
    true
  )

  // Desktop: checkbox toggle — sync tag bar before theme renderActiveTag runs.
  document.addEventListener(
    'input',
    function (e) {
      var input = e.target
      if (!input || !input.classList || !input.classList.contains('tag__input')) return
      if (input.classList.contains('grouped-size-input')) return
      if (isAvailabilityFilter(input.name)) return

      var form = input.closest('.filter-form')
      if (!form || form.hasAttribute('data-mobile-filter')) return

      var wrapper = form.closest('.filter-wrapper')
      var paramName = input.name
      var paramValue = input.value

      if (input.checked) {
        var parentLi = input.closest('li')
        if (parentLi) parentLi.classList.add(ACTIVE_TAG)
        if (wrapper) appendStandardFilterTag(wrapper, getFilterLabel(input), paramName, paramValue)
      } else {
        var uncheckedLi = input.closest('li')
        if (uncheckedLi) uncheckedLi.classList.remove(ACTIVE_TAG)
        removeActiveTagsForFilter(paramName, paramValue, document)
      }

      syncFilterCollapsibleForGroup(input.closest('[data-filter-label]'))

      if (input.closest('[data-filter-type="category"]')) {
        var sidebar = document.querySelector('#CollectionSidebarFilterWrap')
        syncStyleFilterVisibility(sidebar || document)
        scheduleRefreshDesktopSizeGroupsAfterCategoryChange(sidebar || document)
        if (input.checked) {
          refreshStyleFilterOptions(sidebar || document)
        }
      }
    },
    true
  )

  // Desktop grouped size: keep Size parent collapsible in sync after checkbox toggles.
  document.addEventListener(
    'input',
    function (e) {
      var input = e.target
      if (!input || !input.classList || !input.classList.contains('grouped-size-input')) return

      var form = input.closest('.filter-form')
      if (!form || form.hasAttribute('data-mobile-filter')) return

      syncFilterCollapsibleForGroup(form.querySelector('[data-filter-label="Size"]'))
    },
    true
  )

  /**
   * After an AJAX grid reload item-grid.js keeps the desktop sidebar (only the product
   * column is swapped, so the grouped Size filter survives). That left Brand, Color,
   * Category… showing the previous state's counts: "CuddleFields (40)" after picking a
   * size the brand has no stock in, then "0 products". Copy each option's count and
   * visibility from the fresh render instead. Size (grouped), Style (own category-scoped
   * refresh), Price and Availability keep their own handling. Checked options stay
   * visible so a selection can always be undone.
   */
  var STANDARD_COUNT_SYNC_SKIP = {
    Size: true,
    Style: true,
    Price: true,
    Availability: true,
    'Product Department': true
  }

  // Standard rows (text-filter.liquid) render the count as a bare text node after
  // .tag__text: "<span class=tag__text>Label</span> (N)". If that markup ever moves the
  // number into its own span, this lookup must follow or counts silently stop syncing.
  function standardOptionCountNode(li) {
    var textEl = li.querySelector('.tag__text')
    var wrapper = textEl && textEl.parentElement
    if (!wrapper) return null
    var nodes = wrapper.childNodes
    for (var i = nodes.length - 1; i >= 0; i--) {
      if (nodes[i].nodeType === 3 && /\(\d+\)/.test(nodes[i].textContent)) return nodes[i]
    }
    return null
  }

  /**
   * Open panels only — shrink/grow height after rows are hidden; does not change selections or open state.
   * Keep in sync with refreshMobileOpenCollapsibleHeights in collection-mobile-filters.js.
   */
  function refreshDesktopOpenCollapsibleHeights(root) {
    var wrapper =
      root && root.classList && root.classList.contains('filter-wrapper')
        ? root
        : getDesktopFilterWrapper(root)
    if (!wrapper) return

    wrapper.querySelectorAll('[data-filter-label]').forEach(function (groupEl) {
      var trigger = groupEl.querySelector('.collapsible-trigger')
      var content = groupEl.querySelector('.collapsible-content')
      if (
        !trigger ||
        !content ||
        !trigger.classList.contains('is-open') ||
        !content.classList.contains('is-open')
      ) {
        return
      }

      var inner = content.querySelector('.collapsible-content__inner')
      if (!inner) return

      var autoHeight =
        content.classList.contains('collapsible--auto-height') ||
        trigger.classList.contains('collapsible--auto-height')
      content.style.height = autoHeight ? 'auto' : inner.offsetHeight + 'px'
    })
  }

  function syncStandardFilterCounts(sourceRoot, targetRoot) {
    if (!isDesktopSidebarContext()) return
    var targetWrapper = getDesktopFilterWrapper(targetRoot)
    var sourceWrapper =
      sourceRoot && sourceRoot.querySelector
        ? sourceRoot.querySelector('#CollectionSidebarFilterWrap .filter-wrapper') ||
          sourceRoot.querySelector('.filter-wrapper')
        : null
    if (!targetWrapper || !sourceWrapper || sourceWrapper === targetWrapper) return

    var sourceGroups = {}
    sourceWrapper.querySelectorAll('[data-filter-label]').forEach(function (group) {
      sourceGroups[group.getAttribute('data-filter-label') || ''] = group
    })

    targetWrapper.querySelectorAll('[data-filter-label]').forEach(function (targetGroup) {
      var label = targetGroup.getAttribute('data-filter-label') || ''
      if (!label || STANDARD_COUNT_SYNC_SKIP[label]) return
      if (targetGroup.querySelector('.grouped-size-filter') || targetGroup.querySelector('.price-range')) return

      // A group missing from the fresh render has every option at 0 for this state.
      var sourceGroup = sourceGroups[label] || null
      var sourceRows = {}
      if (sourceGroup) {
        sourceGroup.querySelectorAll('li.tag .tag__input[name]').forEach(function (input) {
          sourceRows[input.name + '\u0000' + input.value] = input.closest('li')
        })
      }

      var changed = false
      targetGroup.querySelectorAll('li.tag .tag__input[name]').forEach(function (input) {
        if (input.classList.contains('grouped-size-input')) return
        if (input.classList.contains('grouped-size-dept-input')) return
        var li = input.closest('li')
        if (!li) return
        var srcLi = sourceRows[input.name + '\u0000' + input.value] || null

        var countNode = standardOptionCountNode(li)
        if (countNode) {
          var srcNode = srcLi ? standardOptionCountNode(srcLi) : null
          var text = srcNode ? srcNode.textContent : ' (0)'
          if (countNode.textContent !== text) {
            countNode.textContent = text
            changed = true
          }
        }

        var visible = input.checked || (srcLi !== null && !srcLi.classList.contains('hide'))
        if (li.classList.contains('hide') === visible) {
          li.classList.toggle('hide', !visible)
          changed = true
        }
      })

      if (changed) syncFilterCollapsibleForGroup(targetGroup)
    })

    refreshDesktopOpenCollapsibleHeights(targetWrapper)
  }

  function onCollectionReloaded() {
    // After AJAX grid updates; initial load is handled in item-grid/collection-template init().
    clearDesktopSizeFilterCaches()
    ensureStandardFilterTagIcons()
    syncDesktopFilterCollapsibles()
    syncDesktopPriceActiveTags()
    // Re-enable after removeDesktopPriceFilter temporarily disabled inputs for AJAX.
    setPriceInputsDisabled(document, false)

    var sidebar = document.querySelector('#CollectionSidebarFilterWrap')
    syncStandardFilterCheckboxesFromUrl(sidebar || document)
    syncStyleFilterVisibility(sidebar || document)
    refreshStyleFilterOptions(sidebar || document)
    refreshDesktopSizeGroupsAfterCategoryChange(sidebar || document)
    scheduleFlushPendingGridReloadAfterSizeScope()
    scheduleDepartmentGroupPrefetch(sidebar || document)
  }

  function onFilterSelected() {
    var sidebar = document.querySelector('#CollectionSidebarFilterWrap')
    syncStyleFilterVisibility(sidebar || document)
  }

  window.__syncFilterVisualState = syncFilterVisualState
  window.__removeActiveTagsForFilter = removeActiveTagsForFilter
  window.__ensureStandardFilterTagIcons = ensureStandardFilterTagIcons
  window.__isStandardFilterTag = isStandardFilterTag
  window.__isGroupedSizeTag = isGroupedSizeTag
  window.__isAvailabilityActiveTag = isAvailabilityActiveTag
  window.__cloneFilterCloseIcon = cloneCloseIcon
  window.__getOrCreateActiveTagsList = getOrCreateActiveTagsList
  window.__getFilterLabel = getFilterLabel
  window.__syncGroupedSizeActiveTagsBar = syncGroupedSizeActiveTagsBar
  window.__buildGszFromContainer = buildGszFromContainer
  window.__removeGroupedSizeFromContainer = removeGroupedSizeFromContainer
  window.__syncDesktopFilterCollapsibles = syncDesktopFilterCollapsibles
  window.__syncPriceActiveTag = syncPriceActiveTag
  window.__syncDesktopPriceActiveTags = syncDesktopPriceActiveTags
  window.__getPriceRangeBounds = getPriceRangeBounds
  window.__isPriceSelectionActive = isPriceSelectionActive
  window.__isPriceActiveTag = isPriceActiveTag
  window.__isLegacyServerPriceActiveTag = isLegacyServerPriceActiveTag
  window.__removePriceActiveTagsFromList = removePriceActiveTagsFromList
  window.__resetPriceRangeUI = resetPriceRangeUI
  window.__applyPriceRangeValues = applyPriceRangeValues
  window.__syncStyleFilterVisibility = syncStyleFilterVisibility
  window.__refreshStyleFilterOptions = refreshStyleFilterOptions
  window.__collectCategoryOnlyParams = collectCategoryOnlyParams
  window.__categoryHasActiveSelection = categoryHasActiveSelection
  window.__isCategoryFilterValue = isCategoryFilterValue
  window.__isStyleFilterValue = isStyleFilterValue
  window.__sanitizeCategoryStyleSearchParams = sanitizeCategoryStyleSearchParams
  window.__syncStandardFilterCheckboxesFromUrl = syncStandardFilterCheckboxesFromUrl
  window.__scheduleRefreshDesktopSizeGroupsAfterCategoryChange =
    scheduleRefreshDesktopSizeGroupsAfterCategoryChange
  window.__syncStandardFilterCounts = syncStandardFilterCounts
  window.__groupedSizeRefreshOpenDepartmentGroupCounts = function (root) {
    var mobileEl = document.querySelector('collection-mobile-filters')
    if (mobileEl && typeof mobileEl.refreshOpenDepartmentGroupCounts === 'function') {
      var mobileFw =
        mobileEl.querySelector('.filter-wrapper.is-active') ||
        mobileEl.querySelector('.filter-wrapper')
      if (
        mobileFw &&
        root &&
        root !== document &&
        (root === mobileFw || root.contains(mobileFw) || mobileFw.contains(root))
      ) {
        void mobileEl.refreshOpenDepartmentGroupCounts(mobileFw)
        return
      }
    }
    refreshOpenDesktopDepartmentGroupCounts(root)
  }

  document.addEventListener('filter:selected', onFilterSelected)

  document.addEventListener(
    'toggle',
    function (evt) {
      if (!isDesktopSidebarContext()) return
      var details = evt.target
      if (!details || details.tagName !== 'DETAILS') return
      if (!details.classList.contains('grouped-size-filter__group-details')) return
      if (!details.open) return
      if (details.closest('[data-mobile-filter]')) return

      var groupEl = details.closest('.grouped-size-filter__group[data-dept-value]')
      var sidebar = details.closest('#CollectionSidebarFilterWrap')
      if (!groupEl || !sidebar) return

      var filterWrapper = sidebar.querySelector('.filter-wrapper') || sidebar
      syncDesktopDepartmentGroupCounts(filterWrapper, groupEl)
    },
    true
  )

  // Desktop: hovering a group header starts its render a beat before the click opens it.
  // mouseover fires for every child crossed inside the summary, so remember the last group.
  var lastHoverWarmedGroup = null
  document.addEventListener('mouseover', function (evt) {
    if (!isDesktopSidebarContext() || !evt.target || !evt.target.closest) return
    var summary = evt.target.closest('.grouped-size-filter__group-details > summary')
    if (!summary || summary.closest('[data-mobile-filter]')) return
    var sidebar = summary.closest('#CollectionSidebarFilterWrap')
    if (!sidebar) return
    var groupEl = summary.closest('.grouped-size-filter__group[data-dept-value]')
    if (!groupEl || groupEl === lastHoverWarmedGroup) return
    lastHoverWarmedGroup = groupEl
    warmDepartmentGroupSection(sidebar.querySelector('.filter-wrapper') || sidebar, groupEl)
  })

  // Desktop: opening the Size filter is the signal to warm every department render.
  document.addEventListener('click', function (evt) {
    if (!isDesktopSidebarContext() || !evt.target || !evt.target.closest) return
    var trigger = evt.target.closest('[data-filter-label="Size"] .collapsible-trigger')
    if (!trigger || trigger.closest('[data-mobile-filter]') || !trigger.closest('#CollectionSidebarFilterWrap')) return
    setTimeout(function () {
      scheduleDepartmentGroupPrefetch(document.querySelector('#CollectionSidebarFilterWrap') || document)
    }, 0)
  })

  document.addEventListener('price-range:change', function (e) {
    if (window.__mmSuppressPriceRangeChange) return

    var form = e.target && e.target.closest && e.target.closest('.filter-form')
    if (!form || form.hasAttribute('data-mobile-filter')) return

    // item-grid renders the grid first; refresh the pill href after URL updates.
    requestAnimationFrame(function () {
      var wrapper = form.closest('.filter-wrapper')
      if (wrapper) syncPriceActiveTag(wrapper)

      var priceRange = form.querySelector('.price-range')
      if (priceRange) {
        syncFilterCollapsibleForGroup(priceRange.closest('[data-filter-label]'))
      }
    })
  })

  document.addEventListener('DOMContentLoaded', function () {
    syncDesktopFilterCollapsibles()
    syncDesktopPriceActiveTags()
    setPriceInputsDisabled(document, false)

    var sidebar = document.querySelector('#CollectionSidebarFilterWrap')
    syncStandardFilterCheckboxesFromUrl(sidebar || document)
    syncStyleFilterVisibility(sidebar || document)
    if (categoryHasActiveSelection(sidebar || document)) {
      refreshStyleFilterOptions(sidebar || document)
      scheduleRefreshDesktopSizeGroupsAfterCategoryChange(sidebar || document)
    }
    scheduleDepartmentGroupPrefetch(sidebar || document)
  })
  document.addEventListener('collection:reloaded', onCollectionReloaded)
})()
